import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { useSessionAttendance, useUpdateAttendance, useBulkRecordAttendance } from '@/hooks/useAttendance'
import { useBatch } from '@/hooks/useBatches'
import { scheduleService } from '@/services/scheduleService'
import { enrollmentService } from '@/services/enrollmentService'
import type { AttendanceStatus } from '@/types/attendance'
import type { Schedule } from '@/types/schedule'

const ATTENDANCE_LABELS: Record<AttendanceStatus, string> = {
    present: 'Hadir',
    late: 'Terlambat',
    absent: 'Tidak Hadir',
    excused: 'Izin',
}

export const InstructorAttendancePage: React.FC = () => {
    const { batchId } = useParams<{ batchId: string }>()
    const navigate = useNavigate()

    const [selectedSessionId, setSelectedSessionId] = useState<string>('')
    const [localStatuses, setLocalStatuses] = useState<Record<string, AttendanceStatus>>({})
    const [batchSchedules, setBatchSchedules] = useState<Schedule[]>([])
    const [loadingSchedules, setLoadingSchedules] = useState(true)

    const { data: attendanceResponse, isLoading: loadingAttendance, refetch: refetchAttendance } = useSessionAttendance(selectedSessionId)
    const updateAttendance = useUpdateAttendance()
    const bulkRecordAttendance = useBulkRecordAttendance()
    const { data: batchData } = useBatch(batchId || '')
    const initializedSessionRef = useRef<string>('')

    // Fetch schedules for this batch
    useEffect(() => {
        if (!batchId) return
        setLoadingSchedules(true)
        scheduleService.getSchedulesByBatch(batchId)
            .then((res) => {
                setBatchSchedules(res.data || [])
            })
            .catch(() => {
                setBatchSchedules([])
            })
            .finally(() => {
                setLoadingSchedules(false)
            })
    }, [batchId])

    // Auto-create attendance records when session is selected
    useEffect(() => {
        if (!selectedSessionId || !batchId || !batchData?.data) return
        if (initializedSessionRef.current === selectedSessionId) return

        const batch = batchData.data as any
        const courseId = batch.courseId || batch.course_id
        if (!courseId) return

        const initAttendance = async () => {
            try {
                const enrollRes = await enrollmentService.getByCourse(courseId)
                const enrollments = enrollRes.data || []
                if (enrollments.length === 0) return

                const now = new Date().toISOString()
                const attendances = enrollments.map((e: any) => ({
                    batchId: batchId,
                    studentId: e.userId || e.user_id,
                    trainerId: '',
                    status: 'absent' as AttendanceStatus,
                    timestamp: now,
                }))

                initializedSessionRef.current = selectedSessionId
                await bulkRecordAttendance.mutateAsync({
                    sessionId: selectedSessionId,
                    input: { attendances },
                })
                refetchAttendance()
            } catch (err) {
                initializedSessionRef.current = selectedSessionId
            }
        }

        initAttendance()
    }, [selectedSessionId, batchId, batchData?.data])

    const handleStatusClick = (attendanceId: string, status: AttendanceStatus) => {
        setLocalStatuses((prev) => ({ ...prev, [attendanceId]: status }))
    }

    const handleSaveAttendance = async () => {
        const entries = Object.entries(localStatuses)
        if (entries.length === 0) return

        await Promise.all(
            entries.map(([id, status]) =>
                updateAttendance.mutateAsync({ id, input: { status } })
            )
        )
        setLocalStatuses({})
        refetchAttendance()
    }

    const isSaving = updateAttendance.isPending
    const selectedSchedule = batchSchedules.find(s => s.id === selectedSessionId)

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-8">
                {/* Back button */}
                <button
                    onClick={() => navigate('/instructor/batches')}
                    className="flex items-center gap-2 text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white mb-6 transition-colors"
                >
                    <span className="material-symbols-outlined text-[18px]">arrow_back</span>
                    Kembali ke My Batches
                </button>

                <PageHeader
                    title="Kehadiran"
                    subtitle={(batchData?.data as any)?.title || `Batch ${batchId?.slice(0, 8)}…`}
                />

                {/* Session List */}
                <div className="mb-8">
                    <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Pilih Jadwal Sesi</h2>
                    {loadingSchedules ? (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                        </div>
                    ) : batchSchedules.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {batchSchedules.map((schedule: Schedule) => {
                                const isSelected = selectedSessionId === schedule.id
                                const sessionDate = new Date(schedule.dateTime)
                                const isPast = sessionDate < new Date()

                                return (
                                    <button
                                        key={schedule.id}
                                        onClick={() => {
                                            setSelectedSessionId(schedule.id)
                                            setLocalStatuses({})
                                        }}
                                        className={`text-left p-4 rounded-xl border transition-all ${
                                            isSelected
                                                ? 'bg-primary border-primary shadow-lg shadow-primary/20'
                                                : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-white/5 hover:border-primary/30'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                                <span className={`size-7 rounded-lg flex items-center justify-center text-[10px] font-black ${isSelected ? 'bg-white/20 text-white' : 'bg-primary/10 text-primary'}`}>
                                                    S{schedule.sessionNumber}
                                                </span>
                                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${isSelected ? 'bg-white/20 text-white' : 'bg-slate-100 dark:bg-white/5 text-slate-500'}`}>
                                                    {schedule.type === 'theory' ? 'Teori' : 'Praktik'}
                                                </span>
                                            </div>
                                            <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${isSelected ? 'bg-white/20 text-white' : isPast ? 'bg-slate-100 dark:bg-white/5 text-slate-400' : 'bg-blue-50 dark:bg-blue-500/10 text-blue-500'}`}>
                                                {isPast ? 'Selesai' : 'Mendatang'}
                                            </span>
                                        </div>
                                        <p className={`text-sm font-bold mb-1 ${isSelected ? 'text-white' : 'text-slate-900 dark:text-white'}`}>
                                            {schedule.title}
                                        </p>
                                        <p className={`text-xs ${isSelected ? 'text-white/70' : 'text-slate-400'}`}>
                                            {sessionDate.toLocaleDateString('id-ID', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })} • {sessionDate.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}
                                        </p>
                                    </button>
                                )
                            })}
                        </div>
                    ) : (
                        <div className="p-8 rounded-xl border border-dashed border-slate-200 dark:border-white/10 text-center">
                            <span className="material-symbols-outlined text-[32px] text-slate-300 mb-2">event_busy</span>
                            <p className="text-sm text-slate-500">Belum ada jadwal sesi untuk batch ini.</p>
                        </div>
                    )}
                </div>

                {/* Divider */}
                {selectedSessionId && <div className="border-t border-slate-200 dark:border-white/10 mb-8" />}

                {/* Attendance Data */}
                {selectedSessionId && (
                    <div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                            <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                                Data Kehadiran
                                {selectedSchedule && (
                                    <span className="ml-2 text-sm font-normal text-slate-400">
                                        — {selectedSchedule.title}
                                    </span>
                                )}
                            </h2>
                            <div className="flex items-center gap-2 w-full sm:w-auto">
                                <button
                                    onClick={() => navigate(`/instructor/batches/${batchId}/attendance-scan`)}
                                    className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 dark:bg-white/5 text-slate-700 dark:text-slate-300 rounded-xl font-bold text-sm hover:bg-slate-200 dark:hover:bg-white/10 transition-colors"
                                >
                                    <span className="material-symbols-outlined text-[18px]">qr_code_scanner</span>
                                    Scan QR
                                </button>
                                <button
                                    onClick={handleSaveAttendance}
                                    disabled={isSaving || Object.keys(localStatuses).length === 0}
                                    className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl font-bold text-sm hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                                >
                                    {isSaving ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            Menyimpan...
                                        </>
                                    ) : (
                                        <>
                                            <span className="material-symbols-outlined text-[18px]">save</span>
                                            Simpan Kehadiran
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        {loadingAttendance ? (
                            <div className="flex justify-center py-12">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                            </div>
                        ) : (attendanceResponse?.data || []).length === 0 ? (
                            <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                                <span className="material-symbols-outlined text-[40px] text-slate-300 mb-3">person_search</span>
                                <p className="font-bold mb-1">Tidak ada data kehadiran</p>
                                <p className="text-sm">Data kehadiran akan dibuat otomatis saat sesi dipilih.</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {(attendanceResponse?.data || []).map((attendance) => {
                                    const effectiveStatus = localStatuses[attendance.id] ?? attendance.status
                                    const hasChanged = localStatuses[attendance.id] !== undefined

                                    return (
                                        <div key={attendance.id} className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl bg-white dark:bg-slate-900 border transition-colors ${hasChanged ? 'border-primary/30 bg-primary/5 dark:bg-primary/5' : 'border-slate-100 dark:border-white/5'}`}>
                                            <div className="flex items-center gap-3">
                                                <div className="size-10 rounded-full bg-gradient-to-br from-primary/40 to-primary/20 flex items-center justify-center">
                                                    <span className="text-sm font-bold text-primary">
                                                        {attendance.studentId.charAt(0).toUpperCase()}
                                                    </span>
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold text-slate-900 dark:text-white">
                                                        Siswa: {attendance.studentId.slice(0, 8)}…
                                                    </p>
                                                    <p className="text-xs text-slate-400">
                                                        {new Date(attendance.timestamp).toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long' })}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                                {(['present', 'late', 'absent'] as AttendanceStatus[]).map((status) => (
                                                    <button
                                                        key={status}
                                                        onClick={() => handleStatusClick(attendance.id, status)}
                                                        className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all ${
                                                            effectiveStatus === status
                                                                ? status === 'present' ? 'bg-green-500 text-white' : status === 'late' ? 'bg-yellow-500 text-white' : 'bg-red-500 text-white'
                                                                : 'bg-slate-100 dark:bg-white/5 text-slate-500 hover:bg-slate-200 dark:hover:bg-white/10'
                                                        }`}
                                                    >
                                                        {ATTENDANCE_LABELS[status]}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default InstructorAttendancePage
